import { EnvBanner } from '@common/components/env-banner';
import { ErrorDialog } from '@common/components/error-dialog';
import { Footer } from '@common/components/footer';
import { GeneralError } from '@common/components/general-error/general-error';
import { GlobalRegistrationRequiredModal } from '@common/components/global-registration-required-modal';
import { ImprintModal } from '@common/components/imprint-modal';
import { PrivacyModal } from '@common/components/privacy-modal';
import { EmailNNotVerifiedCountProvider } from '@common/context/email-not-verified-count-context/email-not-verified-count-context';
import { HeaderContextProvider } from '@common/context/header-context';
import { LanguageProvider } from '@common/context/language-provider';
import { LegacyFunctionProvider } from '@common/context/legacy-function-provider';
import { NavigationItemsContextProvider } from '@common/context/navigation-items-context/navigation-items-context';
import { SentryContextProvider } from '@common/context/sentry-context';
import { SpaceContextProvider } from '@common/context/space';
import { SubNavigationProvider } from '@common/context/sub-navigation-context-provider';
import { UserProvider } from '@common/context/user';
import { useSentry } from '@common/hooks/use-sentry';
import { useSpaceDomain } from '@common/hooks/use-space-domain';
import { useLegalModalStore } from '@common/stores';
import { ToastVariant } from '@common/types';
import { LoadUserFn } from '@common/types/load-user-fn';
import { MIME_TYPE_FILE_SIZES } from '@config/mime-type-file-sizes';
import { IResponse, ISurveyStage } from '@konova/api-types';
import { DataLossProvider } from '@konova/dataloss/data-loss-provider';
import { DirtyProvider } from '@konova/dataloss/dirty-provider';
import { ConfigProvider } from '@konova/ui/config-provider';
import { ToastStack } from '@konova/ui/toast-stack';
import { useResizeObserver } from '@konova/ui/use-resize-observer';
import { useToast } from '@konova/ui/use-toast';
import { SpaceAdminRoutes } from '@routes/space-admin-routes';
import { SurveyRoutes } from '@routes/survey-routes';
import * as Sentry from '@sentry/react';
import i18next from 'i18next';
import { FC, useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { generatePath, ScrollRestoration, useLocation, useMatch, useNavigate, useParams } from 'react-router';
import PrivacyStatementModal from 'src/components/PrivacyStatementModal';
import SpaceMaster from 'src/components/RouteMasters/SpaceMaster';
import { FALLBACK_LANGUAGE, FALLBACK_PRIVACY_PDF_URL, SENTRY_DO_NOT_REPORT_ERRORS } from 'src/constants';
import { getDateFnsLocales } from '@konova/i18n/get-date-fns-locales';
import { HealthCheckService } from 'src/services/HealthCheckService';
import { UserService } from 'src/services/UserService';
import {
  IFooterProps,
  IMainContextProps,
  ISpace,
  ISurvey,
  ISurveyCategory,
  IUser
} from 'src/types';
import { sanitizeCssClassName } from 'src/utils';
import StyledAlert from '../Alert';
import Header from '../Header';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v8';

export const Main: FC = () => {
  const spaceDomain = useSpaceDomain();
  const hostname = useSpaceDomain();
  useSentry();

  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { t } = useTranslation();
  const pathSegments = location.pathname.split('/');

  const [initialUserIsLoaded, setInitialUserIsLoaded] = useState<boolean>(false);
  const [user, setUser] = useState<IUser | undefined>(undefined);
  const [loginUri, setLoginUri] = useState<string>('');
  const [activeSurveyStage, setActiveStage] = useState<ISurveyStage | undefined | null>(
    undefined
  );

  const [languages, setLanguages] = useState<string[]>(['de']);
  const [onNavbarBrandClick, setOnNavbarBrandClick] = useState<any | undefined>(
    undefined
  );
  const [showNicknameInHeaderAccountModal, setShowNicknameInHeaderAccountModal] = useState<boolean>(false);

  const [footerProps, setFooterProps] = useState<IFooterProps>({
    organizationData: { termsOfUsePdfUrl: FALLBACK_PRIVACY_PDF_URL },
  });

  const [termsOfUsePdfUrlForPrivacyModal, setTermsOfUsePdfUrlForPrivacyModal] = useState<
    string | undefined
  >(undefined);

  //Toasts
  // const [toastList, setToastList] = useState<any>([]);
  //Global Loading
  const [loading, setLoading] = useState<boolean>(false);
  const [isApiAvailable, setIsApiAvailable] = useState<boolean>(true);
  const [survey, setSurvey] = useState<ISurvey | null>(null);
  const [categories, setCategories] = useState<ISurveyCategory[]>([]);
  const [isSentryEnabled, setIsSentryEnabled] = useState<boolean>(false);
  const [emailVerificationActivated, setEmailVerificationActivated] =
    useState<boolean>(false);
  const [twoFaActivated, setTwoFaActivated] = useState<boolean>(false);
  const [space, setSpace] = useState<ISpace | undefined>(undefined);
  const [spaceLoading, setSpaceLoading] = useState<boolean>(false);
  const [isCustomJsSet, setIsCustomJsSet] = useState<boolean>(false);
  const modalOrganisationData = useLegalModalStore(state => state.organisation);
  const legalPdfUrl = useLegalModalStore(state => state.legalPdfUrl);
  const setPdfUrl = useLegalModalStore(state => state.setPdfUrl);
  const setModalOrganisation = useLegalModalStore(state => state.setOrganisation);
  const isSpaceAdminRoute = useMatch(SpaceAdminRoutes.ALL);
  const isSurveyAdminRoute = useMatch(SurveyRoutes.ADMIN_ALL);

  useEffect(() => {
    window.addEventListener('load', () => {
      document.body.classList.add(
        `env-${import.meta.env.VITE_APP_ENV}`
      );
    });
  }, []);

  useEffect(() => {
    (async () => {
      if (lang && lang.length === 2) {
        await i18next.changeLanguage(lang);
        document.documentElement.setAttribute('lang', lang);
      }
    })();

  }, [lang]);

  useEffect(() => {
    const userSavedLanguage = localStorage.getItem('userLanguage');

    if (
      space?.languages &&
      userSavedLanguage &&
      !lang &&
      space?.languages.includes(userSavedLanguage)
    ) {
      navigate(`/${userSavedLanguage}${location.pathname}${location.search || ''}`);
      document.documentElement.setAttribute('lang', userSavedLanguage);
    }

    if (
      !userSavedLanguage &&
      ((space?.languages && !lang) || (space?.languages && lang?.length !== 2))
    ) {
      navigate(`/${space?.languages[0]}${location.pathname}${location.search || ''}`);
      document.documentElement.setAttribute('lang', space?.languages[0]);

      const browserLanguage = navigator.language.split('-')[0];
      console.log('browserLanguage', browserLanguage);

      if (space?.languages.includes(browserLanguage)) {
        localStorage.setItem('userLanguage', browserLanguage);
        navigate(`/${browserLanguage}${location.pathname}${location.search || ''}`);
      } else {
        localStorage.setItem('userLanguage', space?.languages[0]);
        navigate(
          `/${space?.languages[0]}${location.pathname}${location.search || ''}`
        );
      }
    } else if (
      userSavedLanguage &&
      lang &&
      userSavedLanguage !== lang &&
      lang.length === 2
    ) {
      localStorage.setItem('userLanguage', lang);
      document.documentElement.setAttribute('lang', lang);
    } else if (
      !userSavedLanguage &&
      lang &&
      userSavedLanguage !== lang &&
      lang.length === 2
    ) {
      localStorage.setItem('userLanguage', lang);
      document.documentElement.setAttribute('lang', lang);
    }
  }, [space]);

  i18next.on('initialized', function (options) {
    if (!lang) {
      const fallbackLng = options.fallbackLng && options.fallbackLng[0];
      navigate(`/${fallbackLng}${pathSegments.join('/')}`);
    }
  });

  useEffect(() => {
    healthCheck();
  }, []);

  useEffect(() => {
    setModalOrganisation({
      organization: space?.name,
      organization_address: space?.organization_address,
      organization_city: space?.organization_city,
      organization_address2: space?.organization_address2,
      organization_phone: space?.organization_phone,
      organization_mail: space?.organization_mail,
      organization_website: space?.organization_website,
      organization_zip: space?.organization_zip,
      infoLink: survey ? {
        text: survey?.name ?? '',
        url: survey?.info_link ?? ''
      } : null,
      logoUrl: space?.logoUrl,
      termsOfUsePdfUrl: space?.termsOfUsePdfUrl ?? FALLBACK_PRIVACY_PDF_URL,
    });
    setPdfUrl(space?.termsOfUsePdfUrl ?? null);
  }, [space]);

  useEffect(() => {
    let resolvedPromise = () => Promise.resolve();
    if (
      location.pathname.indexOf('first-logon') > -1 &&
      location.pathname.split('/').lastIndexOf('first-logon') > 2 &&
      location.hash !== '#from_access_code_form' &&
      location.hash !== '#activate'
    ) {
      resolvedPromise = () => UserService.logout().then(() => localStorage.clear());
    }
    resolvedPromise().then(() => loadUser());
  }, []);

  useEffect(() => {
    const regx = new RegExp('\\b' + 'survey-slug' + '[^ ]*[ ]?\\b', 'g');
    document.body.className = document.body.className.replace(regx, '');
    if (survey?.slug) {
      document.body.classList.add(`survey-slug-${survey.slug}`);
    }
  }, [survey]);

  useEffect(() => {
    const regx = new RegExp('\\bcategory-[^ ]*[ ]?\\b', 'g');
    document.body.className = document.body.className.replace(regx, '');

    if (
      categories &&
      categories.length > 0 &&
      survey?.survey_category_id &&
      languages &&
      languages.length > 0
    ) {
      let categoryName = '';

      const surveyCategory = categories.find(
        category => category.id == survey.survey_category_id
      );

      if (surveyCategory) {
        if (surveyCategory.is_default) {
          categoryName = sanitizeCssClassName(
            t('pages.admin.setup.survey-category.default-name', {
              lng: languages[0],
            })
          );
        } else {
          categoryName = sanitizeCssClassName(surveyCategory.name);
        }
      }

      if (categoryName !== '') {
        document.body.classList.add(`category-${categoryName}`);
      }
    }
  }, [survey, categories, languages]);

  useEffect(() => {
    if (!user) {
      setOnNavbarBrandClick(undefined);
    }
    Sentry.withScope(function (scope) {
      if (user?.id) {
        scope.setUser({ id: `${user.id}` });
      } else {
        scope.setUser(null);
      }
    });
    if (user?.platform_role == 1) {
      document.body.classList.add('platform-admin');
    } else {
      document.body.classList.remove('platform-admin');
    }
  }, [user]);

  useEffect(() => {
    if (user?.spaceUser?.role == 1 || survey?.userRole === 'admin') {
      document.body.classList.add('is-administrator');
    }

    if (!user) {
      document.body.classList.remove('is-administrator');
    }
  }, [user, survey]);


  useEffect(() => {
    setTimeout(() => {
      if (!isCustomJsSet && space?.custom_js) {
        const script = document.getElementById('custom-js');
        if (script) {
          script.innerHTML = 'try{' + space.custom_js + '}catch(e){}';
          document.body.insertBefore(script, document.body.firstChild);
        }
        setIsCustomJsSet(true);
      }
    }, 0);
  }, [space]);

  const defineContentPosition = () => {
    const rootNode = document.getElementById('root');
    const headerNode = document.getElementById('header');
    const isNavbarOpened =
      !!headerNode?.getElementsByClassName('navbar-collapse show').length;
    const isSubNavShowed = !!headerNode?.getElementsByClassName('sub-navigation').length;
    // 16 is padding of brand wrapper, we cant take whole header wrapper height there cuz we need only not expandable part of  header, a.k.a first line
    if (rootNode && headerNode) {
      let headerHeight = 0;

      if (!isNavbarOpened && isSubNavShowed) {
        headerHeight =
          headerNode?.getElementsByClassName('brand-wrapper')[0]?.clientHeight + 16;
      }
      if (isNavbarOpened && isSubNavShowed) {
        headerHeight =
          headerNode?.getElementsByClassName('brand-wrapper')[0]?.clientHeight +
          16 +
          headerNode?.getElementsByClassName('sub-navigation')[0]?.clientHeight;
      }
      if (isNavbarOpened && !isSubNavShowed) {
        headerHeight =
          headerNode?.getElementsByClassName('brand-wrapper')[0]?.clientHeight + 16;
      }
      if (!isNavbarOpened) {
        headerHeight = headerNode?.clientHeight;
      }

      rootNode.style.paddingTop = headerHeight + 'px';
      rootNode.style.setProperty('--theme--tokens--header--height', headerHeight + 'px');
    }
  };

  useResizeObserver('#header', defineContentPosition);

  const healthCheck = () => {
    handleRequest(HealthCheckService.healthCheck()).catch(() => setIsApiAvailable(false));
  };

  const openPrivacyModal = (termsOfUsePdfUrl?: string) => {
    setPdfUrl(termsOfUsePdfUrl ?? null);
    setTermsOfUsePdfUrlForPrivacyModal(termsOfUsePdfUrl || FALLBACK_PRIVACY_PDF_URL);
  };

  const getLoginRedirectPath = () => {
    const referrer = encodeURIComponent(
      `${location.pathname}${location.search}${location.hash}`
    );
    return `${loginUri || generatePath(SurveyRoutes.login, { lang: lang!, surveySlug: survey?.slug ?? '' })}?referrer=${referrer}`;
  };

  const loadUser: LoadUserFn = callBack => {
    return new Promise<IUser>(resolve => {
      handleRequest(UserService.getUser(spaceDomain), true, true)
        .then((res: IResponse) => {
          const { body } = res;
          setUser(body.data);
          resolve(body.data);
          return res;
        })
        .catch((err: IResponse) => (callBack ? callBack(err) : null))
        .finally(() => {
          setInitialUserIsLoaded(true);
        });
    });
  };

  const addToast: IMainContextProps['addToast'] = (text, variant = 'success') => {
    toast.add(text, variant as ToastVariant);
  };

  const handleRequest = <T = IResponse,>(
    promise,
    suppressGlobalErrorToast?,
    dontRedirectToLoginOnAuthError?
  ) => {
    return new Promise<T>((resolve, reject) => {
      setLoading(true);
      promise
        .then((response: T) => {
          resolve(response);
        })
        .catch((response: IResponse) => {
          switch (true) {
            case response.statusCode === 401 || response.statusCode === 403:
              if (response.body.errorCode === 4) {
                navigate(
                  `/${survey ? survey.slug : 'admin'}/email-not-verified`
                );
              } else if (response.body.errorCode !== 1) {
                setUser(undefined);
                if (!dontRedirectToLoginOnAuthError) {
                  const rootNode = document.getElementById('root');
                  if (rootNode) rootNode.style.paddingTop = '0px';
                  navigate(getLoginRedirectPath(), { state: { userNotAuthorised: true } });
                }
              }
              if (
                response.body.errorCode === 2 &&
                location.pathname.split('/')[3] !== 'first-logon' &&
                location.pathname.split('/')[4] !== 'first-logon' &&
                location.pathname.split('/')[4] !== 'email-verification'
              ) {
                //const twoFASetupUrl = localStorage.getItem('2fa_setup_page');

                //if (twoFASetupUrl) {
                //  navigate(`/${lang}/${twoFASetupUrl}`);
                //} else {
                // const twoFASetupUrl = location.pathname
                //   .replace(`\/${lang}\/`, '')
                //  .replace(/\/[^\/]*$/, '/2fa-setup')
                //   .replace(/^\//, '');
                //localStorage.setItem('2fa_setup_page', twoFASetupUrl);
                if (isSpaceAdminRoute) {
                  navigate(generatePath(SpaceAdminRoutes.TwoFASetup, { lang: lang ?? FALLBACK_LANGUAGE }));
                } else if (isSurveyAdminRoute && survey) {
                  navigate(generatePath(SurveyRoutes.TwoFASetup, { lang: lang ?? FALLBACK_LANGUAGE, surveySlug: isSurveyAdminRoute.params.surveySlug! }));
                } else {
                  navigate(generatePath(SurveyRoutes.TwoFASetup, { lang: lang ?? FALLBACK_LANGUAGE, surveySlug: survey?.slug ?? '' }));
                }
                //}
              }
              if (response.body.errorCode === 3) {
                const twoFALoginUrl = localStorage.getItem('2fa_login_page');
                if (twoFALoginUrl) {
                  navigate(`/${lang}/${twoFALoginUrl}`);
                }
              }
              break;
            case response.statusCode === 429:
              if (!suppressGlobalErrorToast)
                addToast(t<string>('common.too-many-request'), 'error');
              break;
            default:
              if (!suppressGlobalErrorToast)
                addToast(t<string>('common.error-appeared'), 'error');
          }
          if (
            isSentryEnabled &&
            SENTRY_DO_NOT_REPORT_ERRORS.indexOf(response.statusCode) === -1
          ) {
            Sentry.captureMessage('API error ' + response.statusCode, {
              level: 'error',
              extra: {
                response,
              },
              tags: {
                environment: import.meta.env.MODE,
              },
            });
          }
          return reject(response);
        })
        .finally(() => {
          setLoading(false);
        });
    });
  };

  const toHideHeaderAndFooter = () => {
    if (
      location.pathname.split('/')[3] === 'login' ||
      location.pathname.split('/')[3] === 'reset-pw' ||
      location.pathname.split('/')[2] === 'reset-pw' ||
      location.pathname.split('/')[3] === 'first-logon' ||
      location.pathname.split('/')[4] === 'first-logon' ||
      location.pathname.split('/')[4] === 'email-verification' ||
      location.pathname.split('/')[3] === 'email-not-verified' ||
      location.pathname.split('/')[3] === 'email-verification' ||
      location.pathname.split('/')[3] === '2fa-setup' ||
      location.pathname.split('/')[3] === '2fa-login' ||
      location.pathname.split('/')[2] === '2fa-setup' ||
      location.pathname.split('/')[3] === 'sso-login-providers' ||
      location.pathname.split('/')[4] === 'sso-login-providers'
    )
      return true;
    return false;
  };

  if (!initialUserIsLoaded) return null;

  if (!isApiAvailable) {
    return (
      <Container>
        <Row>
          <Col md={12}>
            <StyledAlert
              show={true}
              title={
                'Die E-Mitwirkung steht zurzeit nicht zur Verfügung. Die Betreiberin wurde automatisch informiert und wird den Dienst schnellstmöglich wiederherstellen. Bitte versuchen Sie es später erneut. Besten Dank für Ihr Verständnis.'
              }
              variant="danger"
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <StyledAlert
              show={true}
              title={
                'Ce projet E-Mitwirkung n’est pas disponible actuellement. Le gestionnaire a été informé automatiquement et rétablira le service dès que possible. Nous vous invitons à réessayer ultérieurement. Merci de votre compréhension.'
              }
              variant="danger"
            />
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <NuqsAdapter>
      <LanguageProvider lang={lang ?? FALLBACK_LANGUAGE} languages={languages}>
        <EnvBanner />
        <ScrollRestoration />
        <SentryContextProvider enabled={isSentryEnabled}>
          <ConfigProvider
            mimeTypeFileSizes={MIME_TYPE_FILE_SIZES}
            datePicker={{ locale: getDateFnsLocales() }}
          >
            <NavigationItemsContextProvider>
              <HeaderContextProvider>
                <DirtyProvider>
                  <DataLossProvider>
                    <ToastStack />
                    <LegacyFunctionProvider
                      handleRequest={handleRequest}
                      addToast={addToast}
                      openPrivacyModal={openPrivacyModal}
                    >
                      <SubNavigationProvider>
                        <SpaceContextProvider>
                          <UserProvider user={user} loadUser={loadUser}>
                            <EmailNNotVerifiedCountProvider>
                              <PrivacyModal pdfUrl={legalPdfUrl} />
                              <ImprintModal organizationData={modalOrganisationData} />
                              <GlobalRegistrationRequiredModal />
                              <ErrorDialog />
                              {toHideHeaderAndFooter() ? null : (
                                <ErrorBoundary fallbackRender={({ error }) => <GeneralError error={error as Error} />}>
                                  <Header
                                    survey={survey}
                                    onNavBarBrandClick={onNavbarBrandClick}
                                    loading={loading}
                                    hostname={hostname}
                                    setIsSentryEnabled={setIsSentryEnabled}
                                    emailVerificationActivated={emailVerificationActivated}
                                    twoFaActivated={twoFaActivated}
                                    spaceLoading={spaceLoading}
                                  />
                                </ErrorBoundary>
                              )}
                              <main className="content" id="main-content">
                                <SpaceMaster
                                  survey={survey}
                                  setIsSentryEnabled={setIsSentryEnabled}
                                  setTwoFaActivated={setTwoFaActivated}
                                  setLoginUri={setLoginUri}
                                  setFooterProps={props => setFooterProps(props)}
                                  setLanguages={setLanguages}
                                  footerProps={footerProps}
                                  setSurvey={setSurvey}
                                  setActiveSurveyStage={setActiveStage}
                                  activeSurveyStage={activeSurveyStage}
                                  setEmailVerificationActivated={setEmailVerificationActivated}
                                  setContextCategories={setCategories}
                                  setOnNavbarBrandClick={setOnNavbarBrandClick}
                                />
                              </main>
                              {toHideHeaderAndFooter() ? null : (
                                <Footer
                                  organisation={footerProps.organizationData}
                                  lang={lang!}
                                />
                              )}

                              {termsOfUsePdfUrlForPrivacyModal ? (
                                <PrivacyStatementModal
                                  handleClose={() => {
                                    setTermsOfUsePdfUrlForPrivacyModal(undefined);
                                  }}
                                  show={true}
                                  termsOfUsePdfUrl={termsOfUsePdfUrlForPrivacyModal}
                                />
                              ) : null}
                            </EmailNNotVerifiedCountProvider>
                          </UserProvider>
                        </SpaceContextProvider>
                      </SubNavigationProvider>
                    </LegacyFunctionProvider>
                  </DataLossProvider>
                </DirtyProvider>
              </HeaderContextProvider>
            </NavigationItemsContextProvider>
          </ConfigProvider>
        </SentryContextProvider >
      </LanguageProvider>
    </NuqsAdapter>
  );
};